import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Send, Star } from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubmitTestimonial() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    location: '',
    story: '',
    rating: 0,
    allow_publication: true
  });

  const [hoverRating, setHoverRating] = useState(0);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.age || !formData.location || !formData.story) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    // Convert age to number for validation
    const age = parseInt(formData.age, 10);
    if (isNaN(age) || age < 18 || age > 100) {
      toast.error('L\'âge doit être entre 18 et 100 ans');
      return;
    }

    if (formData.story.trim().length < 50) {
      toast.error('Votre histoire doit contenir au moins 50 caractères');
      return;
    }

    if (formData.story.length > 1000) {
      toast.error('Votre histoire ne peut pas dépasser 1000 caractères');
      return;
    }

    if (formData.rating === 0) {
      toast.error('Veuillez donner une note');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      await axios.post(
        `${API}/testimonials/submit`,
        {
          name: formData.name,
          age: parseInt(formData.age),
          location: formData.location,
          story: formData.story,
          rating: formData.rating,
          allow_publication: formData.allow_publication
        },
        { headers }
      );

      toast.success('Merci ! Votre témoignage a été soumis avec succès. Il sera examiné par notre équipe avant publication.');
      
      // Reset form
      setFormData({
        name: '',
        age: '',
        location: '',
        story: '',
        rating: 0,
        allow_publication: true
      });

      // Redirect to testimonials page after 2 seconds
      setTimeout(() => {
        navigate(`/${language}/testimonials`);
      }, 2000);

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, i) => {
      const starValue = i + 1;
      return (
        <button
          key={i}
          type="button"
          onClick={() => setFormData(prev => ({ ...prev, rating: starValue }))}
          onMouseEnter={() => setHoverRating(starValue)}
          onMouseLeave={() => setHoverRating(0)}
          className="transition-transform hover:scale-110"
        >
          <Star
            className={`w-10 h-10 ${
              starValue <= (hoverRating || formData.rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      );
    });
  };

  const characterCount = formData.story.length;
  const characterLimit = 1000;

  return (
    <>
      <Helmet>
        <title>Partagez Votre Histoire | A-More AI</title>
        <meta name="description" content="Partagez votre expérience avec A-More AI. Votre témoignage peut inspirer d'autres célibataires à trouver l'amour." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen py-8 px-6">
        <div className="max-w-3xl mx-auto">
          <button
            onClick={() => navigate(`/${language}/testimonials`)}
            className="flex items-center gap-2 text-gray-600 hover:text-pink-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour aux témoignages
          </button>

          <div className="glass-card p-8">
            <div className="flex items-center justify-center mb-6">
              <Heart className="w-12 h-12 text-pink-500 fill-pink-500" />
            </div>

            <h1 className="text-3xl font-bold gradient-text mb-4 text-center">
              Partagez Votre Histoire
            </h1>

            <p className="text-gray-600 text-center mb-8">
              Avez-vous trouvé l'amour grâce à A-More AI ? Partagez votre expérience pour inspirer d'autres célibataires !
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Votre prénom ou pseudo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="Sophie M."
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Votre nom complet ne sera pas affiché publiquement
                </p>
              </div>

              {/* Age and Location */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="age" className="block text-sm font-semibold text-gray-700 mb-2">
                    Âge <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="age"
                    name="age"
                    value={formData.age}
                    onChange={handleChange}
                    min="18"
                    max="100"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="29"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2">
                    Ville <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    placeholder="Paris"
                    required
                  />
                </div>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Votre note globale <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {renderStars()}
                  {formData.rating > 0 && (
                    <span className="ml-4 text-lg font-semibold text-gray-700">
                      {formData.rating}/5
                    </span>
                  )}
                </div>
              </div>

              {/* Story */}
              <div>
                <label htmlFor="story" className="block text-sm font-semibold text-gray-700 mb-2">
                  Votre histoire <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="story"
                  name="story"
                  value={formData.story}
                  onChange={handleChange}
                  rows="8"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                  placeholder="Racontez-nous comment A-More AI vous a aidé à trouver l'amour... Comment s'est passée votre rencontre ? Qu'est-ce qui vous a plu dans l'application ?"
                  required
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">
                    Minimum 50 caractères
                  </p>
                  <p className={`text-xs ${characterCount > characterLimit ? 'text-red-500' : 'text-gray-500'}`}>
                    {characterCount}/{characterLimit}
                  </p>
                </div>
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="allow_publication"
                  name="allow_publication"
                  checked={formData.allow_publication}
                  onChange={handleChange}
                  className="mt-1"
                />
                <label htmlFor="allow_publication" className="text-sm text-gray-600">
                  J'autorise A-More AI à publier ce témoignage sur le site et les réseaux sociaux. Mon témoignage sera examiné par l'équipe avant publication.
                </label>
              </div>

              {/* Privacy Notice */}
              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-gray-700">
                  <strong>Confidentialité :</strong> Votre témoignage sera examiné par notre équipe avant publication. Seul votre prénom/pseudo, votre âge et votre ville seront affichés publiquement. Vos informations de contact ne seront jamais partagées.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-4 flex items-center justify-center gap-2 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Envoyer mon témoignage
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
